/** Tuning of each enemy type (GDD §8.2). The behaviours live in src/core/enemies. */
export interface GallinazoTuning {
  /** Speed to the left in world coordinates, on top of the scroll (u/s). */
  readonly relativeSpeed: number;
  /** Vertical sine (u and Hz), from the tick it becomes active. */
  readonly amplitude: number;
  readonly frequencyHz: number;
  /** Radius of the circular hitbox (u). */
  readonly hitboxRadius: number;
  /** How it is announced (GDD §8.1). EN-01 enters at nearly the scroll speed and needs no INTERCEPT. */
  readonly announce: 'intercept' | 'own-telegraph' | 'none';
  /** EN-23 (#106): offsets of the wingmen of the V from the leader (u). */
  readonly spacingX?: number;
  readonly spacingY?: number;
}

/** EN-03 Avioneta sicaria (GDD §8.2, issue #85): flies straight at the height the pilot had at the warning. */
export interface HitmanPlaneTuning {
  /** Speed to the left on top of the scroll (u/s). */
  readonly extraSpeed: number;
  /** AABB hitbox (u), 70-80 % of the model. */
  readonly hitboxWidth: number;
  readonly hitboxHeight: number;
  readonly announce: 'intercept' | 'own-telegraph' | 'none';
}

/** EN-02 Helicóptero de la ley (GDD §8.2, issue #84): hovers and sweeps a searchlight; it never falls. */
export interface LawHelicopterTuning {
  readonly hitboxWidth: number;
  readonly hitboxHeight: number;
  readonly announce: 'intercept' | 'own-telegraph' | 'none';
  /** Full aperture of the cone (degrees) and the range it sweeps to each side of straight down. */
  readonly coneApertureDeg: number;
  readonly sweepRangeDeg: number;
  /** One sweep (there and back) and the staying times in the cone (seconds). */
  readonly sweepSeconds: number;
  readonly amberAfterSeconds: number;
  readonly burstAfterSeconds: number;
  readonly projectilesPerBurst: number;
  readonly projectileSpeed: number;
  /** Radius of the projectile circle (u) and ticks between the shots of a burst. */
  readonly projectileRadius: number;
  readonly projectileSpacingTicks: number;
  /** Speed of the arrival from above (u/s) and the height it starts from. */
  readonly arrivalSpeed: number;
  readonly arrivalFromY: number;
}

/** EN-04 Sicario en parapente (GDD §8.2, issue #86): a slow descent with a lateral drift that blocks a gap for a while. */
export interface ParagliderTuning {
  readonly hitboxWidth: number;
  readonly hitboxHeight: number;
  readonly announce: 'intercept' | 'own-telegraph' | 'none';
  /** Descent speed (u/s) and the default drift: a sine of this amplitude (u) and frequency (Hz). */
  readonly descentSpeed: number;
  readonly driftAmplitude: number;
  readonly driftHz: number;
  /**
   * Veneno's motor paraglider (GDD §4.3, §10.3, issue #98; `params.motor = 1`): it flies level to the left at
   * this speed on top of the scroll, above the 2 u/s threshold of INTERCEPT (§8.1), and never lands.
   */
  readonly motorSpeed: number;
}

/** EN-11 Hipopótamo de la Hacienda (GDD §8.3, issue #87): bubbles, a ballistic jump and a splash back. */
export interface HippoTuning {
  readonly hitboxWidth: number;
  readonly hitboxHeight: number;
  /** It announces itself with its own bubbles, not with INTERCEPT. */
  readonly announce: 'intercept' | 'own-telegraph' | 'none';
  readonly bubblesLeadSeconds: number;
  readonly apexY: number;
  /** Surface of the lagoon: where it leaves and where it goes back (u). */
  readonly lagoonY: number;
}

/** EN-12 Teleférico (GDD §8.3, issue #88): a cabin that goes back and forth along a cable; the cable is harmless. */
export interface CableCarTuning {
  readonly hitboxWidth: number;
  readonly hitboxHeight: number;
  readonly announce: 'intercept' | 'own-telegraph' | 'none';
  /** Cabin speed along the cable when the level does not say (u/s). */
  readonly defaultSpeed: number;
}

/**
 * EN-21 Grúa portuaria (GDD §8.3, issue #104): a structure in the place of a column, with two passages of the
 * world 2 gap (3.7 u) on either side of a central beam. Its arm pivots on the beam and points 45° up (blocking
 * the high passage) or 45° down (blocking the low one); it swaps every 3 s with a 0.5 s rotation, announced by
 * a beacon 1.0 s before. The mast is the hitbox width; the arm is a chain of circles of `armRadius`.
 */
export interface PortCraneTuning {
  /** Width of the mast and height of the central beam (u): the hitbox of the fixed structure. */
  readonly hitboxWidth: number;
  readonly hitboxHeight: number;
  readonly announce: 'intercept' | 'own-telegraph' | 'none';
  readonly passageHeight: number;
  readonly armLength: number;
  readonly armRadius: number;
  readonly armAngleDeg: number;
  readonly periodSeconds: number;
  readonly rotationSeconds: number;
  readonly beaconLeadSeconds: number;
}

/**
 * EN-31 Fumigadora (GDD §8.3, issue #116): a slow crop duster that crosses the screen leaving a toxic cloud. It flies
 * with the player at `worldSpeed` (so it drifts left on screen at scroll minus that and needs no INTERCEPT) and sprays
 * a puff every `sprayEverySeconds` that stays fixed in the world and hurts for `puffSeconds`. The body is an AABB; a
 * puff is a circle of `puffRadius`. The cloud counts as an enemy projectile for the power-ups (GDD §7).
 */
export interface CropDusterTuning {
  readonly hitboxWidth: number;
  readonly hitboxHeight: number;
  readonly announce: 'intercept' | 'own-telegraph' | 'none';
  /** Speed in the world, in the direction of the player (u/s). */
  readonly worldSpeed: number;
  readonly sprayEverySeconds: number;
  readonly puffRadius: number;
  readonly puffSeconds: number;
}

/**
 * EN-22 Antena del "KGB de Cali" (GDD §8.3, issue #105): a surveillance zone, harmless by itself, that calls an
 * EN-03 2 s after the plane touches it (INTERCEPT 1 s before). It has no hitbox of its own: it is mounted on a
 * piece of a column (width and height 0 are never used).
 */
export interface AntennaTuning {
  readonly hitboxWidth: number;
  readonly hitboxHeight: number;
  readonly announce: 'intercept' | 'own-telegraph' | 'none';
  readonly zoneRadius: number;
  readonly delaySeconds: number;
  readonly warningLeadSeconds: number;
}

/**
 * EN-32 Sombrero volador (GDD §8.3, issue #117): Don Nieto throws a sombrero that flies out like a boomerang
 * and comes back to the balcony. It is a circle; the balcony and Don Nieto have no collider of their own.
 */
export interface SombreroTuning {
  /** Radius of the sombrero circle (u), 70-80 % of the model (GDD §8.1). */
  readonly hitboxRadius: number;
  /** Wind-up before each throw (seconds). */
  readonly windupSeconds: number;
  /** Time between throws while the balcony is ahead (seconds). */
  readonly cadenceSeconds: number;
  /** Reach of the outbound path to the left of the balcony (u). */
  readonly reach: number;
  /** Total flight time (seconds): half out, half back. */
  readonly flightSeconds: number;
  /** Vertical drop of the return below the outbound height (u). */
  readonly returnDrop: number;
  readonly announce: 'intercept' | 'own-telegraph' | 'none';
}

/**
 * EN-33 Tren del tequila (GDD §8.3, issue #118): a ground train whose chimney smoke blocks the view but never
 * hurts. The body is an AABB on the ground; the smoke is render-only (depth exception of §14.1).
 */
export interface TrainTuning {
  /** AABB of the body in u, 70-80 % of the model (GDD §8.1). It rests on the ground. */
  readonly hitboxWidth: number;
  readonly hitboxHeight: number;
  /** Visual bounds of the model (u), for the 70-80 % hitbox rule. */
  readonly visualWidth: number;
  readonly visualHeight: number;
  /** Speed with the player, on top of the scroll (u/s). Slower than the scroll, so no INTERCEPT. */
  readonly worldSpeed: number;
  /** Seconds between smoke columns and the life of a column (s). */
  readonly smokeEverySeconds: number;
  readonly smokeSeconds: number;
  /** Rise speed of the smoke (u/s) and its max opacity when drawn (<= 0.6). */
  readonly smokeRiseSpeed: number;
  readonly smokeOpacity: number;
  readonly announce: 'intercept' | 'own-telegraph' | 'none';
}

/**
 * EN-41 Águila real (GDD §8.3, issue #127): a golden eagle that dives from above towards the pilot's height at
 * warning time, announced by a screech and a shadow. It enters from above, so no INTERCEPT.
 */
export interface GoldenEagleTuning {
  /** Warning before the dive (seconds): the screech and the shadow. */
  readonly warningSeconds: number;
  /** Position of the eagle when the dive starts: this far ahead of the pilot (u) and this high (u). */
  readonly spawnAhead: number;
  readonly spawnAbove: number;
  /** Dive speed along the line and horizontal speed once it levels off (u/s). */
  readonly diveSpeed: number;
  readonly leaveSpeed: number;
  /** Radius of the circular hitbox (u), 70-80 % of the model (GDD §8.1). */
  readonly hitboxRadius: number;
  readonly announce: 'intercept' | 'own-telegraph' | 'none';
}

/**
 * EN-42 Dron fronterizo (GDD §8.3, issue #128): a Border Patrol drone that patrols vertically between two heights
 * at 3 u/s with an exact rebound. It is a hazard, never destroyed.
 */
export interface BorderDroneTuning {
  /** AABB in u, 70-80 % of the model (GDD §8.1). */
  readonly hitboxWidth: number;
  readonly hitboxHeight: number;
  /** Vertical patrol speed (u/s). */
  readonly patrolSpeed: number;
  readonly announce: 'intercept' | 'own-telegraph' | 'none';
}

/**
 * EN-43 Túnel con trampilla (GDD §8.3, issue #129): dust 1.0 s ahead, then a barrel launched straight up to an
 * apex and back into the tunnel. The barrel is a circle; the dust and the trapdoor do not collide.
 */
export interface TunnelTuning {
  /** Dust warning before the launch (seconds). */
  readonly dustLeadSeconds: number;
  /** Apex height of the barrel (u). */
  readonly apexY: number;
  /** The tunnel triggers when the plane is within this distance (u). */
  readonly triggerDistance: number;
  /** Radius of the barrel circle (u), 70-80 % of the model (GDD §8.1). */
  readonly hitboxRadius: number;
  readonly announce: 'intercept' | 'own-telegraph' | 'none';
}

/**
 * EN-44 Reflector del muro (GDD §8.3, §8.2, issue #130): an EN-02 without the helicopter, fixed on the wall, with
 * several units sweeping in sync. It inherits every cone, amber and burst value from EN-02; only its own data row
 * exists here. Per-instance `params.syncGroup` and `params.phaseOffset` set the sweep offset.
 */
export interface WallSearchlightTuning {
  readonly announce: 'intercept' | 'own-telegraph' | 'none';
}

export const ENEMIES = Object.freeze({
  'EN-01': { relativeSpeed: 1, amplitude: 0.5, frequencyHz: 1, hitboxRadius: 0.4, announce: 'none' },
  // EN-23 Gaviota (GDD §8.3, issue #106): EN-01 in a V of three, announced as one flock with INTERCEPT.
  'EN-23': { relativeSpeed: 1, amplitude: 0.5, frequencyHz: 1, hitboxRadius: 0.4, announce: 'intercept', spacingX: 1, spacingY: 0.8 },
  'EN-12': { hitboxWidth: 1.2, hitboxHeight: 1, announce: 'none', defaultSpeed: 2 },
  'EN-11': { hitboxWidth: 1.4, hitboxHeight: 0.9, announce: 'own-telegraph', bubblesLeadSeconds: 1.2, apexY: 5, lagoonY: 1 },
  'EN-04': { hitboxWidth: 0.8, hitboxHeight: 1, announce: 'none', descentSpeed: 1, driftAmplitude: 0.5, driftHz: 0.5, motorSpeed: 3 },
  'EN-02': {
    hitboxWidth: 2,
    hitboxHeight: 1,
    announce: 'none',
    coneApertureDeg: 30,
    sweepRangeDeg: 30,
    sweepSeconds: 2,
    amberAfterSeconds: 0.4,
    burstAfterSeconds: 0.8,
    projectilesPerBurst: 3,
    projectileSpeed: 6,
    projectileRadius: 0.1,
    projectileSpacingTicks: 9,
    arrivalSpeed: 4,
    arrivalFromY: 13,
  },
  'EN-03': { extraSpeed: 4, hitboxWidth: 1.4, hitboxHeight: 0.5, announce: 'intercept' },
  'EN-31': { hitboxWidth: 1.4, hitboxHeight: 0.5, announce: 'none', worldSpeed: 2, sprayEverySeconds: 0.25, puffRadius: 0.5, puffSeconds: 4 },
  'EN-22': { hitboxWidth: 0, hitboxHeight: 0, announce: 'none', zoneRadius: 3, delaySeconds: 2, warningLeadSeconds: 1 },
  'EN-32': { hitboxRadius: 0.4, windupSeconds: 0.6, cadenceSeconds: 2.5, reach: 6, flightSeconds: 2, returnDrop: 1, announce: 'own-telegraph' },
  'EN-33': { hitboxWidth: 4.5, hitboxHeight: 1.5, visualWidth: 6, visualHeight: 2, worldSpeed: 1.5, smokeEverySeconds: 1.5, smokeSeconds: 3, smokeRiseSpeed: 2, smokeOpacity: 0.6, announce: 'none' },
  'EN-41': { warningSeconds: 1, spawnAhead: 4, spawnAbove: 13, diveSpeed: 12, leaveSpeed: 12, hitboxRadius: 0.5, announce: 'own-telegraph' },
  'EN-42': { hitboxWidth: 0.8, hitboxHeight: 0.4, patrolSpeed: 3, announce: 'none' },
  'EN-43': { dustLeadSeconds: 1, apexY: 8, triggerDistance: 11, hitboxRadius: 0.45, announce: 'own-telegraph' },
  'EN-44': { announce: 'own-telegraph' },
  'EN-21': {
    hitboxWidth: 1.5,
    hitboxHeight: 1,
    announce: 'own-telegraph',
    passageHeight: 3.7,
    armLength: 6,
    armRadius: 0.3,
    armAngleDeg: 45,
    periodSeconds: 3,
    rotationSeconds: 0.5,
    beaconLeadSeconds: 1,
  },
} as const satisfies Record<
  string,
  GallinazoTuning | HitmanPlaneTuning | LawHelicopterTuning | ParagliderTuning | HippoTuning | CableCarTuning | PortCraneTuning | CropDusterTuning | AntennaTuning | SombreroTuning | TrainTuning | GoldenEagleTuning | BorderDroneTuning | TunnelTuning | WallSearchlightTuning
>);

export type EnemyType = keyof typeof ENEMIES;

export const isEnemyType = (type: string): type is EnemyType => type in ENEMIES;
